import {
  getTaxFullUrl,
  getVerificationBypass,
  isPaulHoodReferral,
  isSyntheticMonitor,
  sanitizeLooseTextInput,
  sanitizeTextInput,
  sanitizeTightTextInput
} from '../../../shared/utils';
import { ReferralFormData } from '../context';
import { ProData } from '../../../shared/types';

interface ReferralResponse {
  request_key: string;
  opportunity_keys: string[];
}

interface ReferralRequestBody {
  taxReferral: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    postalCode: string;
    interests: string[];
    intents: string[];
    questions: string;
    advice: string[];
    taxHistory: string;
    taxConfidence: string;
    importance: string;
    leadSource: string;
    taxSituation: string;
    pros: [
      {
        partnerUrn: string;
        contactName: string;
        companyName: string;
        agreement: string;
      }
    ];
    agreement: string;
    matchKey: string;
    phPersonalIntents?: string[];
    phSelfEmployedSmallBusinessIntent?: string[];
    phTaxIssues?: string[];
    phTaxAdvice?: string[];
    phSomethingElse?: string;
    phUrgency?: string;
  };
  recaptcha: { override?: string; response?: string };
  verificationToken?: string;
  syntheticMonitor: boolean;
}

async function createReferral(
  request: ReferralFormData,
  recaptcha: string,
  proInfo: ProData,
  proAgreement: string,
  ramseyAgreement: string,
  verificationToken?: string,
  baseURL = getTaxFullUrl()
) {
  request.leadSource =
    new URLSearchParams(window.location.search).get('lead_source') ?? '';
  const body = createReferralRequestBody(
    request,
    recaptcha,
    proInfo,
    proAgreement,
    ramseyAgreement,
    verificationToken
  );
  const fullUrl = `${baseURL}/api/referral`;
  const response = await fetch(fullUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  return (await response.json()) as ReferralResponse;
}

function createRecaptcha(responseToken: string) {
  const bypassVerificationQueryParam = getVerificationBypass();
  if (bypassVerificationQueryParam) {
    return { override: bypassVerificationQueryParam };
  }
  return { response: responseToken };
}

function createReferralRequestBody(
  referral: ReferralFormData,
  responseToken: string,
  proInfo: ProData,
  proAgreementText: string,
  ramseyAgreementText: string,
  verificationToken?: string
): ReferralRequestBody {
  const taxReferralRequestBody: ReferralRequestBody = {
    taxReferral: {
      firstName: sanitizeTightTextInput(referral.first_name),
      lastName: sanitizeTightTextInput(referral.last_name),
      email: referral.email,
      phone: referral.phone.replace(/\D/g, ''),
      postalCode: referral.postal_code,
      interests: referral.interests.map(sanitizeTightTextInput),
      intents: referral.tax_intents?.map(sanitizeTightTextInput),
      questions: sanitizeTextInput(referral.tax_questions),
      advice: referral.tax_advice?.map(sanitizeTightTextInput),
      taxHistory: referral.tax_history,
      taxConfidence: referral.tax_confidence,
      importance: referral.importance,
      leadSource: referral.leadSource,
      taxSituation: referral.tax_situation,
      pros: [
        {
          partnerUrn: proInfo.partner_urn,
          contactName: sanitizeTextInput(proInfo.contact_name),
          companyName: sanitizeLooseTextInput(proInfo.company_name),
          agreement: sanitizeLooseTextInput(proAgreementText)
        }
      ],
      agreement: sanitizeLooseTextInput(ramseyAgreementText),
      matchKey: proInfo.match_key
    },
    recaptcha: createRecaptcha(responseToken),
    // Omitted rather than sent empty when absent (the bypass path), so the backend's optional-field
    // validation sees no key at all instead of a value it has to treat as missing.
    ...(verificationToken ? { verificationToken } : {}),
    syntheticMonitor: isSyntheticMonitor()
  };
  if (isPaulHoodReferral(proInfo.account_key)) {
    return appendPHReferralData(taxReferralRequestBody, referral);
  } else {
    return taxReferralRequestBody;
  }
}

function appendPHReferralData(
  referralRequest: ReferralRequestBody,
  referralData: ReferralFormData
): ReferralRequestBody {
  return {
    ...referralRequest,
    taxReferral: {
      ...referralRequest.taxReferral,
      phPersonalIntents: referralData.ph_personal_intents?.map(
        sanitizeTightTextInput
      ),
      phSelfEmployedSmallBusinessIntent:
        referralData.ph_self_employed_small_business_intent?.map(
          sanitizeTightTextInput
        ),
      phTaxIssues: referralData.ph_tax_issues?.map(sanitizeTightTextInput),
      phTaxAdvice: referralData.ph_tax_advice?.map(sanitizeTightTextInput),
      phSomethingElse: sanitizeTextInput(referralData.ph_something_else),
      phUrgency: referralData.ph_urgency
    }
  };
}

export { createReferral, ReferralResponse };
